"use client";
import { activeSiteConfig } from "@config/site/site-config";
import { ClickAndCollect, CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { IconButton } from "@ui/components/forms/icon-button/IconButton";
import { Input } from "@ui/components/forms/input/Input";
import { InputLeftElement, InputRightElement } from "@ui/components/forms/input/InputElement";
import { InputGroup } from "@ui/components/forms/input/InputGroup";
import { Box } from "@ui/components/layout/box/Box";
import { ECCOIcon } from "@ui/components/media-and-icons/ecco-icon/ECCOIcon";
import { sizes } from "@ui/design-tokens";
import type { ChangeEvent, FormEvent } from "react";
import { useCallback, useContext, useEffect, useState, useRef } from "react";
import { Controller } from "./controller";
import { formatStreet } from "./normalize-store";
import { SEARCH } from "@e2e/helpers/store-locator/constants/common";
import { googleMapsUtils } from "@lib/maps/googleMapsUtils";
import { useSearchParams } from "next/navigation";

export const Search = ({ isPickUpSelection }: { isPickUpSelection: boolean }) => {
    const { getMultiple } = useMicrocopy();
    const {
        storeData,
        setSelectedStoreId,
        setSearchResult,
        setLastQuery,
        validStoreId,
        setIsLoading,
        shouldRestrictCountry,
        setFilterDistance,
        filterDistance,
    } = useContext(Controller);
    const searchParams = useSearchParams();

    const [autocompleteState, setAutocompleteState] = useState<any>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const timeoutRef = useRef<any>(null);
    const autocompleteContainerRef = useRef(null);

    const [query, setQuery] = useState<string>(searchParams.get("storeName") || "");

    const { country } = activeSiteConfig;

    const microcopies = (() => {
        return getMultiple(CommonCMS.page, [
            ClickAndCollect.storeLocatorPlaceholder,
            ClickAndCollect.storeLocatorReset,
        ]);
    })();

    const searchStoreById = (storeId: string) => {
        const store = storeData?.find((store) => store?.storeId === storeId);
        if (store) {
            searchStoresByAddress(`${formatStreet(store)}, ${store.postalCode} ${store.city}`);
        }
    };

    const searchStoresByAddress = async (address: string): Promise<void> => {
        setIsLoading(true);
        if (address.trim() === "") {
            setIsLoading(false);
            return;
        }

        try {
            const results = await googleMapsUtils.geocodeAddress(
                address,
                shouldRestrictCountry ? { country } : undefined
            );

            setSelectedStoreId("");
            setSearchResult(results);
        } catch (error) {
            if (error.code === "ZERO_RESULTS") {
                setSearchResult([]);
            }
        } finally {
            setIsLoading(false);
            if (filterDistance) setFilterDistance(null);
        }
    };

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        setLastQuery(query);
        if (validStoreId(query)) {
            searchStoreById(query);
        } else {
            searchStoresByAddress(query);
        }
    };

    const autocompleteOptions = {
        componentRestrictions: { country },
        fields: ["address_components", "geometry", "name"],
        strictBounds: false,
        types: ["establishment", "geocode"],
    };

    useEffect(() => {
        let autocompleteInstance: google.maps.places.Autocomplete | null = null;

        const initializeAutocomplete = async () => {
            if (!inputRef.current) return;

            const handlePlaceSelected = (place: any) => {
                autocompleteContainerRef.current?.replaceChildren();
                setSearchResult([place] as any);
                setQuery(googleMapsUtils.generateAddressQuery(place));
            };

            autocompleteInstance = await googleMapsUtils.initializeAutocomplete(
                inputRef.current,
                autocompleteOptions,
                handlePlaceSelected
            );

            setAutocompleteState(autocompleteInstance);
        };

        initializeAutocomplete();

        return () => {
            if (autocompleteInstance) {
                google.maps.event.clearInstanceListeners(autocompleteInstance);
            }
            googleMapsUtils.cleanup();
        };
    }, []);

    useEffect(() => {
        if (autocompleteState !== null) {
            timeoutRef.current = setTimeout(() => {
                const container = document.querySelector(".pac-container");
                if (container) {
                    autocompleteContainerRef.current = container as HTMLElement;
                    // The autocomplete list is placed outside the main DOM element, we need this to override the z-index and place it on top of the Modal on CC Store locator. Otherwise, set z-index= 4
                    if (!isPickUpSelection) {
                        autocompleteContainerRef?.current?.setAttribute(
                            "style",
                            "z-index: 4; display: none"
                        );
                        autocompleteContainerRef?.current?.classList.add(
                            "store-locator__autocomplete"
                        );
                    }
                }
            }, 750);
        }

        // Cleanup function
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [autocompleteState]);

    const handleReset = useCallback(() => {
        autocompleteContainerRef.current?.replaceChildren();
        setQuery("");
        setLastQuery("");
        setSelectedStoreId("");
        setSearchResult(undefined);
        // We need to remove the childrens manually after clearing the input, otherwise the list will be still in the DOM, just not visible.
    }, [setLastQuery, setSearchResult, setSelectedStoreId]);

    const handleQuery = (event: ChangeEvent<HTMLInputElement>) => setQuery(event.target.value);

    useEffect(() => {
        searchParams.has("storeName") &&
            setTimeout(() => {
                setIsLoading(true);
                searchStoresByAddress(searchParams.get("storeName") as string);
            }, 1000);
    }, [searchParams]);

    return (
        <Box>
            <div>
                <InputGroup>
                    <InputLeftElement
                        position={["absolute"]}
                        w={sizes.icon["4xs"]}
                        h={"full"}
                        zIndex={3}
                    >
                        <ECCOIcon name="search" color="black" />
                    </InputLeftElement>
                    <Input
                        ref={inputRef}
                        data-testid={SEARCH}
                        type="text"
                        name="query"
                        value={query}
                        sx={{
                            px: 10,
                            py: 0,
                            h: ["55px", 10.4],
                            _focusVisible: {
                                outlineColor: "transparent",
                                outline: "none",
                                borderColor: "gray.800  ",
                            },
                            _hover: {
                                outline: "none",
                            },
                            _focus: {
                                outline: "none",
                            },
                            _placeholder: {
                                color: "gray.500",
                            },
                        }}
                        onChange={handleQuery}
                        placeholder={microcopies[ClickAndCollect.storeLocatorPlaceholder]}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                handleSubmit(e);
                            }
                        }}
                    />
                    <InputRightElement h="10.4">
                        {query && (
                            <IconButton
                                variant="ghost"
                                size={"md"}
                                aria-label={microcopies[ClickAndCollect.storeLocatorReset]}
                                icon={<ECCOIcon name="close" color="black" />}
                                onClick={handleReset}
                                // We need this to prevent the focus outline to be outside the input element
                                sx={{ "&:focus-visible": { outlineOffset: "-2px" } }}
                            />
                        )}
                    </InputRightElement>
                </InputGroup>
            </div>
        </Box>
    );
};
